import { useRef, useState } from 'react'
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Descriptions,
  Empty,
  Form,
  Modal,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useDeniedPlans, usePlanOptions } from '@/shared/api/reference'
import { displayName } from '@/shared/lib/planName'
import { Can } from '@/shared/auth/Can'
import { formatDateTime } from '@/shared/lib/format'
import { formatMoneyValue } from '@/shared/lib/money'
import { toMoney } from '@/shared/api/schemas'
import { usePermissions } from '@/shared/auth/usePermissions'
import { salePreview } from '@/features/cashdesk/salesApi'
import { useSubscriberOverview } from './useSubscriberOverview'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { RowActions } from '@/shared/ui/table/RowActions'
import type { OperationResult } from '@/shared/api/operationResult'
import { RemainingTraffic } from './RemainingTraffic'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import { newIdempotencyKey } from '@/shared/lib/idempotency'
import {
  fetchGrantOverview,
  fetchGrants,
  issueGrant,
  revokeGrant,
  type AccessGrant,
  type GrantStateValue,
} from './subscriberApi'

/**
 * Grantlar qancha vaqt "yangi" hisoblanadi.
 *
 * ⚠️ Ular soatlab o'zgarmaydi va faqat ANIQ amaldan keyin o'zgaradi:
 * paket sotish, bekor qilish, balans to'ldirish. Bularning HAMMASI keshni
 * o'zi bekor qiladi — shu fayldagi `refresh()`, `SubscriberFundAction`
 * dagi `subscribers.all` prefiksi va `SubscriberActions` dagi runner.
 * Ya'ni eskirish INVALIDATSIYA orqali boshqariladi, vaqt orqali emas:
 * umumiy 10 soniyalik chegara ro'yxat ↔ kartochka orasida yurganda ikkala
 * so'rovni ham bekorga takrorlardi.
 */
export const GRANT_STALE_MS = 60_000

const GRANT_COLOR: Record<GrantStateValue, string> = {
  PENDING: 'default',
  ACTIVE: 'green',
  EXHAUSTED: 'gold',
  EXPIRED: 'default',
  REVOKED: 'red',
}

interface SubscriberGrantsProps {
  subscriberId: string
  /**
   * Abonentning turi (`HOTSPOT` / `PPPOE`) — paketning yetkazish usuli
   * bilan solishtirish uchun. Berilmasa tekshiruv o'tkazilmaydi.
   */
  subscriberKind?: string
  /** Abonent filiali — sotuv ro'yxatidan shu filialda sotilmaydigan paketlar chiqadi (`null` — filialsiz) */
  branchNodeId?: string | null
}

/**
 * Abonentning paketlari (grantlar).
 *
 * ⚠️ GRANT ≠ SESSIYA (01-DOMAIN-MODEL.md §1). Grant "nimaga haqli" ni
 * aytadi (kvota, muddat), sessiya esa "hozir ulanganmi" ni. Faol grant bor,
 * lekin abonent offline bo'lishi mumkin — va aksincha.
 */
interface SellValues {
  planId: string
  charge: boolean
  allowCredit?: boolean
}

export function SubscriberGrants({ subscriberId, subscriberKind, branchNodeId }: SubscriberGrantsProps) {
  const { t, i18n } = useTranslation()
  // ⚠️ BUG-048 — `modal` KONTEKSTDAN: statik `Modal.confirm` React
  // daraxtidan tashqarida yangi ildiz ochadi va `ConfigProvider`
  // mavzusiga yetmaydi — qorong'i panel ustida OQ dialog chiziladi.
  // Turg'un oyna (paket sotish) React daraxti ICHIDA — unga tegishli emas.
  const { modal } = App.useApp()
  const queryClient = useQueryClient()
  const [sellOpen, setSellOpen] = useState(false)
  const sellKey = useRef(newIdempotencyKey('admin-grant'))
  const openSell = () => {
    sellKey.current = newIdempotencyKey('admin-grant')
    setSellOpen(true)
  }
  const [result, setResult] = useState<OperationResult | null>(null)
  const [form] = Form.useForm<SellValues>()
  const { can } = usePermissions()

  const denied = useDeniedPlans(branchNodeId, sellOpen)
  const plans = usePlanOptions(sellOpen).filter((plan) => plan.sellable)
    .filter((plan) => !denied.has(plan.id))
  const standing = useSubscriberOverview(subscriberId, sellOpen).data
  const chosenPlanId = Form.useWatch('planId', form)
  const charge = Form.useWatch('charge', form) ?? true
  const allowCredit = Form.useWatch('allowCredit', form) ?? false
  const chosenPrice = plans.find((plan) => plan.id === chosenPlanId)?.price ?? null
  const balance = standing?.balance ? toMoney(standing.balance) : null
  const debtLimit = standing?.debtLimit ? toMoney(standing.debtLimit) : null
  const credit =
    charge && chosenPrice && balance && debtLimit
      ? salePreview({ balance: balance.amount, debtLimit: debtLimit.amount, price: chosenPrice.amount, paid: 0, discount: 0 })
      : null
  const creditBlocked =
    credit !== null && credit.debtIncrease > 0 && (credit.overLimit || !can('cash.sale.credit') || !allowCredit)

  /**
   * ⚠️ Bitta «amaldagi grant» o'rniga TO'LIQ manzara.
   *
   * Ilgari bu yerda `fetchActiveGrant` turardi va u bitta grant
   * qaytarardi. Bir abonentda bir necha faol grant bo'lishi normal, va
   * **tezlik** ular ichidan boshqa qoida bo'yicha tanlanadi. Panel
   * bittasini ko'rsatgani uchun operator "tezlikni qaysi grant
   * beryapti" degan savolni bera olmasdi — nuqson shuning uchun jonli
   * tizimda oylab yashadi.
   */
  const overview = useQuery({
    queryKey: queryKeys.subscribers.grantOverview(subscriberId),
    queryFn: () => fetchGrantOverview(subscriberId),
    staleTime: GRANT_STALE_MS,
  })

  const grants = useQuery({
    queryKey: queryKeys.subscribers.grants(subscriberId),
    queryFn: () => fetchGrants(subscriberId),
    staleTime: GRANT_STALE_MS,
  })

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.grants(subscriberId) })
    void queryClient.invalidateQueries({
      queryKey: queryKeys.subscribers.grantOverview(subscriberId),
    })
    // Paket sotilgach balans va kvota o'zgaradi — ro'yxat ham eskiradi
    void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.all })
  }

  const sell = useMutation({
    mutationFn: (values: SellValues) =>
      issueGrant(subscriberId, values.planId, values.charge, sellKey.current, values.allowCredit ?? false),
    onSuccess: () => {
      setResult({ status: 'OK', message: t('billing.sellPackage.done') })
      setSellOpen(false)
      form.resetFields()
      refresh()
    },
    // ⚠️ `onError` ATAYLAB yo'q: xato OYNANING ICHIDA ko'rsatiladi.
    // Ilgari u kartaning yuqorisidagi `OperationResultAlert` ga chiqardi,
    // oyna esa OCHIQ qolardi (`setSellOpen(false)` faqat `onSuccess` da) —
    // ya'ni "Mablag' yetmadi. Kerak: 30 000, mavjud: 12 000" degan eng
    // kerakli xabar niqob ortida turardi.
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin */
  const closeSell = () => {
    sell.reset()
    setSellOpen(false)
  }

  const revoke = useMutation({
    mutationFn: (id: string) => revokeGrant(id),
    // ⚠️ 2026-08-15 (tester CRUD-ACCESS) — ILGARI BU YERDA YOLG'ON BOR EDI.
    //
    // Oldin javob NIMA bo'lishidan qat'i nazar `status: 'OK'` yozilardi.
    // Eng og'ir holat: abonentning OXIRGI paketi bekor qilinsa, server
    // faol sessiyaga hech nima yubormaydi (qo'llaydigan tezlik qolmaydi)
    // va abonent qayta ulanmaguncha internetdan to'liq foydalanaveradi —
    // panel esa yashil "bajarildi" ko'rsatardi.
    //
    // Bu `QARORLAR.md` §3.2, Qoida 4 ning buzilishi edi. Endi holat
    // serverdan keladi va PARTIAL sariq ogohlantirish bo'lib chiqadi.
    //
    // ⚠️ `?? 'OK'` — eski servis bilan moslik (schema izohi). Yangi
    //    maydon kelmasa, xulq ilgarigidek qoladi, buzilmaydi.
    onSuccess: (revoked) => {
      setResult({
        status: revoked.liveSession?.status ?? 'OK',
        message: revoked.liveSession?.message || t('billing.revoke.done'),
      })
      refresh()
    },
    onError: (error) => setResult({ status: 'FAILED', message: translateError(error, t) }),
  })

  const governingId = overview.data?.governingGrantId ?? null
  const consumingId = overview.data?.consumingGrantId ?? null
  const usable = overview.data?.usable ?? []
  const current = usable.find((grant) => grant.id === consumingId) ?? null
  const governing = usable.find((grant) => grant.id === governingId) ?? null

  const columns: ColumnsType<AccessGrant> = [
    {
      // Sahifalanmagan ro'yxat — raqam to'g'ridan-to'g'ri `index` dan
      key: '__rowNo',
      title: t('dataTable.rowNo'),
      width: 56,
      align: 'center',
      render: (_v, _r, index) => <Typography.Text type="secondary">{index + 1}</Typography.Text>,
    },
    {
      key: 'state',
      title: t('subscribers.columns.status'),
      width: 120,
      render: (_, row) => <Tag color={GRANT_COLOR[row.state]}>{t(`grantState.${row.state}`)}</Tag>,
    },
    {
      key: 'role',
      title: t('billing.grant.role'),
      width: 170,
      // ⚠️ Ikki yorliq ATAYLAB alohida: ular bir grantda bo'lishi ham,
      // BOSHQA-boshqa grantlarda bo'lishi ham mumkin va ikkinchisi xato
      // emas — model shunday (tezlik oxirgi tarifdan, hajm esa avval
      // kuyib ketadigan paketdan sarflanadi).
      render: (_, row) => (
        <Space size={4} wrap>
          {row.id === governingId ? <Tag color="blue">{t('billing.grant.roleSpeed')}</Tag> : null}
          {row.id === consumingId ? <Tag color="green">{t('billing.grant.roleQuota')}</Tag> : null}
        </Space>
      ),
    },
    {
      key: 'source',
      title: t('billing.grant.source'),
      width: 120,
      render: (_, row) => t(`grantSource.${row.source}`, { defaultValue: row.source }),
    },
    {
      key: 'quota',
      title: t('billing.grant.quota'),
      width: 160,
      // `null` = cheksiz. "0" deb ko'rsatish tugagan paket bilan adashtirardi
      render: (_, row) => (
        <RemainingTraffic quotaBytes={row.quotaBytes ?? null} consumedBytes={row.consumedBytes} compact />
      ),
    },
    {
      key: 'expiresAt',
      title: t('subscribers.columns.expiresAt'),
      width: 180,
      render: (_, row) => (row.expiresAt ? formatDateTime(row.expiresAt) : '—'),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_, row) => (
        <RowActions
          items={[
            {
              key: 'revoke',
              label: t('billing.revoke.button'),
              permission: 'subscriber.update',
              danger: true,
              loading: revoke.isPending,
              hidden: row.state !== 'ACTIVE',
              onClick: () => {
                modal.confirm({
                  title: t('billing.revoke.title'),
                  // ⚠️ Pul qaytarilmasligini AYTAMIZ: backend uni alohida
                  // qaror deb qoldirgan va operator buni bilishi shart
                  content: t('billing.revoke.confirm'),
                  okButtonProps: { danger: true },
                  okText: t('billing.revoke.ok'),
                  cancelText: t('common.cancel'),
                  onOk: () => revoke.mutate(row.id),
                })
              },
            },
          ]}
        />
      ),
    },
  ]

  return (
    <Card
      title={t('billing.grant.title')}
      extra={
        <Can permission="subscriber.update">
          <Button type="primary" size="small" onClick={openSell}>
            {t('billing.actions.sellPackage')}
          </Button>
        </Can>
      }
    >
      {result ? (
        <div style={{ marginBottom: 16 }}>
          <OperationResultAlert result={result} closable onClose={() => setResult(null)} />
        </div>
      ) : null}

      {/*
        ⚠️ XATO HOLATI — usiz bu karta YOLG'ON GAPIRARDI.

        So'rov yiqilsa `usable` bo'sh qolardi va karta "Faol paket
        yo'q" deb yozardi. Operator buni haqiqat deb qabul qilib,
        abonentga IKKINCHI paket sotishi mumkin edi — pul yechiladi,
        abonentda esa allaqachon paket bor.

        Xato "ma'lumot yo'q" dan butunlay boshqa narsa va ular
        ALBATTA ajratilishi kerak.
      */}
      {overview.isError || grants.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('dataTable.loadFailed')}
          description={translateError(overview.error ?? grants.error, t)}
          action={
            <Button
              size="small"
              onClick={() => {
                void overview.refetch()
                void grants.refetch()
              }}
            >
              {t('dataTable.retry')}
            </Button>
          }
        />
      ) : null}

      {/* ⚠️ "Qancha qoldi?" HAR DOIM ko'rinadi — paket bo'lmaganda ham.
          Ilgari bu ma'lumot faqat grant mavjud bo'lganda chizilardi va
          paketsiz abonentda ekranda umuman hech narsa yo'q edi: operator
          "ma'lumot yuklanmadimi yoki trafik tugadimi?" degan savolga
          javob topolmasdi. Endi uch holatning HAR BIRI aniq matn beradi. */}
      <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
        {/* ⚠️ "Joriy paketda" — yuqoridagi kartadagi YIG'INDI bilan
            adashtirmaslik uchun. Ikkalasi bir xil nom bilan turganda
            ekran o'ziga zid ko'rinardi. */}
        <Descriptions.Item
          label={
            <Tooltip title={t('billing.quota.remainingHint')}>
              <span>{t('billing.quota.remaining')}</span>
            </Tooltip>
          }
        >
          <RemainingTraffic
            hasGrant={current !== null}
            quotaBytes={current?.quotaBytes ?? null}
            consumedBytes={current?.consumedBytes ?? 0}
          />
        </Descriptions.Item>
      </Descriptions>

      {/* ⚠️ Bu blok butun kartochkadagi ENG MUHIM ma'lumot: abonentda
          nechta faol grant bor va ulardan qaysi biri nimani boshqaradi.
          Usiz operator "tezlik qaysi paketdan kelyapti" degan savolni
          BERA OLMASDI va tezlik eskisida qolib ketgani oylab
          sezilmasdi. */}
      {usable.length > 0 ? (
        <>
          {governing === null ? (
            <div style={{ marginBottom: 16 }}>
              <OperationResultAlert
                result={{ status: 'PARTIAL', message: t('billing.grant.ungoverned') }}
              />
            </div>
          ) : null}
          {/*
            ⚠️ BITTA USTUN — VA BU EKRAN KENGLIGIGA BOG'LIQ EMAS.

            Ilgari `column={2}` edi va qiymatlar HARFLAB TIK yozilardi:
            "VOUCHER" o'rniga V-O-U-C-H-E-R. Bu telefon muammosi emas —
            1440px monitorda ham shunday, chunki bu karta sahifaning
            YARIM ustunida turadi va unga ~330px tegadi. Ikki ustun esa
            o'sha 330px ni TO'RTGA bo'ladi.

            Shuning uchun antd ning `{{ xs, sm, md }}` javobi ham yordam
            bermaydi: u OYNA kengligiga qaraydi, kartaniki emas. Yagona
            ishonchli yechim — bitta ustun.
          */}
          <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
            <Descriptions.Item label={t('billing.grant.usableCount')}>
              {usable.length}
            </Descriptions.Item>
            <Descriptions.Item label={t('billing.grant.accessUntil')}>
              {overview.data?.accessUntil
                ? formatDateTime(overview.data.accessUntil)
                : t('limits.noExpiration')}
            </Descriptions.Item>
            <Descriptions.Item label={t('billing.grant.governedBy')}>
              {governing ? (
                <Space size={4} wrap>
                  <Tag color="blue">{governing.source}</Tag>
                  <span>
                    {governing.expiresAt
                      ? formatDateTime(governing.expiresAt)
                      : t('limits.noExpiration')}
                  </span>
                </Space>
              ) : (
                t('billing.grant.noPolicy')
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('billing.grant.consumedFrom')}>
              {current === null ? t('billing.grant.none') : current.source}
            </Descriptions.Item>
            <Descriptions.Item label={t('billing.grant.deliveryMethod')}>
              {current ? t(`deliveryMethod.${current.deliveryMethod}`) : '—'}
            </Descriptions.Item>
          </Descriptions>

          {/*
            ⚠️ PAKET TURI ABONENT TURIGA MOS KELMASA.

            Sinovda topilgan holat: mehmon portali orqali kirgan abonent
            HOTSPOT, unga berilgan paket esa PPPOE uchun edi. To'rt
            hisobda ham shunday, hech qanday belgi yo'q.

            Mexanizmi: portal avval hotspot hisobini yaratadi, keyin
            vaucher O'SHA MAVJUD loginga ulanadi
            (`VoucherServiceImpl.findOrCreate` mavjud hisobni topadi va
            paketning yetkazish usulini tekshirmaydi).

            Bu yerda faqat KO'RSATILADI, bloklanmaydi: mavjud
            nomuvofiqliklarni yashirish yomonroq, lekin panelda qattiq
            xato qilib qo'yish ishlab turgan hisobni "buzuq" deb
            ko'rsatardi. Tuzatish — ma'lumotni to'g'rilash (vaucher bilan
            sotiladigan paket HOTSPOT bo'lishi kerak), keyin yozish
            yo'lida tekshiruv.
          */}
          {current && subscriberKind && current.deliveryMethod !== subscriberKind ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('billing.grant.kindMismatch')}
              description={t('billing.grant.kindMismatchHint', {
                subscriber: t(`deliveryMethod.${subscriberKind}`),
                plan: t(`deliveryMethod.${current.deliveryMethod}`),
              })}
            />
          ) : null}
        </>
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('billing.grant.none')}
          style={{ marginBottom: 16 }}
        />
      )}

      <Table<AccessGrant>
        columns={columns}
        dataSource={grants.data ?? []}
        rowKey={(row) => row.id}
        loading={grants.isPending}
        size="small"
        pagination={false}
        scroll={{ x: 700 }}
      />

      <Modal
        open={sellOpen}
        title={t('billing.sellPackage.title')}
        onCancel={closeSell}
        onOk={() => submitOnValid(form, (values) => sell.mutate(values))}
        confirmLoading={sell.isPending}
        okButtonProps={{ disabled: creditBlocked }}
        okText={t('billing.sellPackage.submit')}
        cancelText={t('common.cancel')}
        destroyOnHidden
      >
        {/* Mablag' yetmasa backend 422 tashlaydi va matnni tanada beradi
            ("Kerak: 30 000, mavjud: 12 000") — uni qayta yozmaymiz */}
        {sell.isError ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message={translateError(sell.error, t)}
          />
        ) : null}

        <Form
          form={form}
          layout="vertical"
          preserve={false}
          initialValues={{ charge: true }}
          onValuesChange={() => {
            sellKey.current = newIdempotencyKey('admin-grant')
          }}
        >
          <Form.Item
            name="planId"
            label={t('billing.sellPackage.choose')}
            rules={[{ required: true, message: t('billing.sellPackage.validationPackage') }]}
          >
            <Select
              autoFocus
              notFoundContent={<Empty description={t('billing.sellPackage.empty')} />}
              options={plans.map((plan) => ({
                value: plan.id,
                label: `${displayName(plan, i18n.language)} — ${formatMoneyValue(plan.price)}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="charge" valuePropName="checked">
            {/* Belgilanmasa `fundedBy: NONE` ketadi — ya'ni sovg'a, balansdan
                pul yechilmaydi. Buni yashirish "nega pul yechilmadi?" degan
                savolga olib kelardi */}
            <Checkbox>{t('billing.sellPackage.charge')}</Checkbox>
          </Form.Item>
          <Space direction="vertical" size={0}>
            <span style={{ opacity: 0.65 }}>{t('billing.sellPackage.chargeHint')}</span>
          </Space>
          {charge && balance && debtLimit ? (
            <Typography.Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0 }}>
              {t('billing.sellPackage.standing', {
                balance: formatMoneyValue(balance),
                limit: formatMoneyValue(debtLimit),
              })}
            </Typography.Paragraph>
          ) : null}
          {credit && credit.debtIncrease > 0 ? (
            <div style={{ marginTop: 12 }}>
              {credit.overLimit ? (
                <Alert
                  type="error"
                  showIcon
                  message={t('sales.creditLimitExceeded', {
                    debt: formatMoneyValue({ amount: credit.outstanding, currency: debtLimit!.currency }),
                    limit: formatMoneyValue(debtLimit!),
                  })}
                />
              ) : can('cash.sale.credit') ? (
                <Alert
                  type="warning"
                  showIcon
                  message={t('sales.creditWarning', {
                    amount: formatMoneyValue({ amount: credit.debtIncrease, currency: debtLimit!.currency }),
                  })}
                  description={
                    <Form.Item name="allowCredit" valuePropName="checked" style={{ marginBottom: 0 }}>
                      <Checkbox>{t('sales.allowCredit')}</Checkbox>
                    </Form.Item>
                  }
                />
              ) : (
                <Alert type="error" showIcon message={t('sales.creditForbidden')} />
              )}
            </div>
          ) : null}
        </Form>
      </Modal>
    </Card>
  )
}
